import React, { useState, useEffect, useCallback, useRef } from 'react';

const SPECIES_INFO: Record<string, { emoji: string; name: string; maturity_h: number; color: string; cost: number }> = {
  amaranth: { emoji: '🌱', name: 'Amaranth',     maturity_h: 4,   color: '#84cc16', cost: 10  },
  vetiver:  { emoji: '🌾', name: 'Vetiver',      maturity_h: 8,   color: '#0891b2', cost: 20  },
  opuntia:  { emoji: '🌵', name: 'Prickly Pear', maturity_h: 16,  color: '#ea580c', cost: 35  },
  moringa:  { emoji: '🌿', name: 'Moringa',      maturity_h: 24,  color: '#65a30d', cost: 50  },
  jujube:   { emoji: '🫐', name: 'Jujube',       maturity_h: 36,  color: '#d97706', cost: 70  },
  acacia:   { emoji: '🌳', name: 'Acacia',       maturity_h: 48,  color: '#15803d', cost: 90  },
  mesquite: { emoji: '🌲', name: 'Mesquite',     maturity_h: 72,  color: '#16a34a', cost: 120 },
  neem:     { emoji: '🌴', name: 'Neem',         maturity_h: 120, color: '#0d9488', cost: 160 },
};

function fmtDuration(ms: number): string {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

interface TreeEntry   { species: string; planted_at: string; }
interface RentalEntry { species: string; planted_at: string; planted_by: string; }
interface PlotData    { owner: string | null; owner_trees: TreeEntry[]; rental_trees: RentalEntry[]; }

interface Props { waxAccount: string; onClose: () => void; inline?: boolean; }

// ── Reusable slot grid ───────────────────────────────────────────────────────
function OwnerSlots({
  slots, isOwn, pickingSlot, setPickingSlot, planting, onHarvest,
}: {
  slots: (TreeEntry | null)[];
  isOwn: boolean;
  pickingSlot: number | null;
  setPickingSlot: (i: number | null) => void;
  planting: boolean;
  onHarvest: (i: number) => void;
}) {
  const now = Date.now();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 8 }}>
      {slots.map((tree, i) => {
        if (tree) {
          const sp = SPECIES_INFO[tree.species];
          const matureAt = new Date(tree.planted_at).getTime() + (sp?.maturity_h ?? 24) * 3600000;
          const mature = now >= matureAt;
          return (
            <div key={i} style={{ aspectRatio: '1', borderRadius: 10, border: `1px solid ${mature && isOwn ? '#4ade80' : (sp?.color ?? '#334155') + '44'}`, background: 'rgba(15,23,42,0.85)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, padding: 4 }}>
              <span style={{ fontSize: 20 }}>{sp?.emoji ?? '🌿'}</span>
              <span style={{ fontSize: 8, color: sp?.color ?? '#94a3b8', fontWeight: 700, textAlign: 'center' }}>{sp?.name ?? tree.species}</span>
              {mature && isOwn
                ? <button onClick={() => onHarvest(i)} style={{ fontSize: 8, color: '#0a1628', background: '#4ade80', border: 'none', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontWeight: 900 }}>Harvest</button>
                : mature
                  ? <span style={{ fontSize: 8, color: '#4ade80', fontWeight: 700 }}>Mature ✓</span>
                  : <span style={{ fontSize: 8, color: '#64748b' }}>{fmtDuration(matureAt - now)}</span>}
            </div>
          );
        }
        const canPlant = isOwn && !planting;
        return (
          <div key={i} onClick={() => canPlant && setPickingSlot(pickingSlot === i ? null : i)}
            style={{
              aspectRatio: '1', borderRadius: 10,
              border: pickingSlot === i ? '1px solid #fbbf24' : '1px dashed rgba(255,255,255,0.12)',
              background: pickingSlot === i ? 'rgba(251,191,36,0.08)' : 'rgba(15,23,42,0.6)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 4, cursor: canPlant ? 'pointer' : 'not-allowed', opacity: canPlant ? 1 : 0.35,
              transition: 'border-color 0.15s',
            }}>
            <span style={{ fontSize: 18, color: canPlant ? '#fbbf24' : '#475569' }}>＋</span>
            <span style={{ fontSize: 9, color: canPlant ? '#d97706' : '#475569' }}>{isOwn ? 'Plant' : 'Not yours'}</span>
          </div>
        );
      })}
    </div>
  );
}

function RentalSlots({
  slots, waxAccount, canPlant, pickingSlot, setPickingSlot, planting, onHarvest,
}: {
  slots: (RentalEntry | null)[];
  waxAccount: string;
  canPlant: boolean;
  pickingSlot: number | null;
  setPickingSlot: (i: number | null) => void;
  planting: boolean;
  onHarvest: (i: number) => void;
}) {
  const now = Date.now();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 8 }}>
      {slots.map((tree, i) => {
        if (tree) {
          const sp = SPECIES_INFO[tree.species];
          const matureAt = new Date(tree.planted_at).getTime() + (sp?.maturity_h ?? 24) * 3600000;
          const mature = now >= matureAt;
          const isPlanter = tree.planted_by === waxAccount;
          return (
            <div key={i} style={{ aspectRatio: '1', borderRadius: 10, border: `1px solid ${mature && isPlanter ? '#4ade80' : (sp?.color ?? '#334155') + '44'}`, background: 'rgba(15,23,42,0.85)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, padding: 4 }}>
              <span style={{ fontSize: 18 }}>{sp?.emoji ?? '🌿'}</span>
              <span style={{ fontSize: 8, color: sp?.color ?? '#94a3b8', fontWeight: 700, textAlign: 'center' }}>{sp?.name ?? tree.species}</span>
              <span style={{ fontSize: 7, color: isPlanter ? '#a78bfa' : '#475569', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{tree.planted_by}</span>
              {mature && isPlanter
                ? <button onClick={() => onHarvest(i)} style={{ fontSize: 8, color: '#0a1628', background: '#4ade80', border: 'none', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontWeight: 900 }}>Harvest</button>
                : mature
                  ? <span style={{ fontSize: 8, color: '#4ade80', fontWeight: 700 }}>Mature ✓</span>
                  : <span style={{ fontSize: 8, color: '#64748b' }}>{fmtDuration(matureAt - now)}</span>}
            </div>
          );
        }
        const active = canPlant && !planting;
        return (
          <div key={i} onClick={() => active && setPickingSlot(pickingSlot === i ? null : i)}
            style={{
              aspectRatio: '1', borderRadius: 10,
              border: pickingSlot === i ? '1px solid #a78bfa' : '1px dashed rgba(167,139,250,0.2)',
              background: pickingSlot === i ? 'rgba(167,139,250,0.08)' : 'rgba(15,23,42,0.5)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 4, cursor: active ? 'pointer' : 'not-allowed', opacity: active ? 1 : 0.35,
              transition: 'border-color 0.15s',
            }}>
            <span style={{ fontSize: 18, color: active ? '#a78bfa' : '#334155' }}>＋</span>
            <span style={{ fontSize: 9, color: active ? '#7c3aed' : '#334155' }}>{canPlant ? 'Plant' : 'No seeds'}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Main modal ───────────────────────────────────────────────────────────────
export default function AtaviForestPlantModal({ waxAccount, onClose, inline = false }: Props) {

  // ── Section A: My Plots (bhoomi trees) ────────────────────────────────────
  const [myPlots, setMyPlots]       = useState<{ x: number; y: number }[]>([]);
  const [totalOwned, setTotalOwned] = useState(0);
  const [xA, setXA]                 = useState('');
  const [yA, setYA]                 = useState('');
  const [plotA, setPlotA]           = useState<PlotData | null>(null);
  const [loadingA, setLoadingA]     = useState(false);
  const [errorA, setErrorA]         = useState('');
  const [selA, setSelA]             = useState<{ x: number; y: number } | null>(null);
  const [bhoomi, setBhoomi]         = useState<number | null>(null);
  const [pickA, setPickA]           = useState<number | null>(null);
  const [statusA, setStatusA]       = useState<'idle'|'planting'|'done'|'error'>('idle');
  const [msgA, setMsgA]             = useState('');
  const partialCursor               = useRef(0);
  const fullCursor                  = useRef(0);

  // ── Section B: Rental Plots (seed trees) ──────────────────────────────────
  const [seeds, setSeeds]           = useState<number | null>(null);
  const [xB, setXB]                 = useState('');
  const [yB, setYB]                 = useState('');
  const [plotB, setPlotB]           = useState<PlotData | null>(null);
  const [loadingB, setLoadingB]     = useState(false);
  const [errorB, setErrorB]         = useState('');
  const [selB, setSelB]             = useState<{ x: number; y: number } | null>(null);
  const [pickB, setPickB]           = useState<number | null>(null);
  const [statusB, setStatusB]       = useState<'idle'|'planting'|'done'|'error'>('idle');
  const [msgB, setMsgB]             = useState('');
  const rentalCursor                = useRef({ x: -1, y: -1 });

  useEffect(() => {
    if (!waxAccount) return;
    fetch(`/samatvam/api/samatvam/atavi-forest/my-plots/${waxAccount}`)
      .then(r => r.json()).then(d => { if (d.success) { setMyPlots(d.plots); setTotalOwned(d.total); } }).catch(() => {});
    fetch(`/samatvam/api/samatvam/samrajya/${waxAccount}`)
      .then(r => r.json()).then(d => { if (d.success) setBhoomi(parseFloat(d.bhoomi_score) || 0); }).catch(() => {});
    fetch(`/samatvam/api/samatvam/seeds/${waxAccount}`)
      .then(r => r.json()).then(d => { if (d.success) setSeeds(d.seeds_in_wallet ?? 0); }).catch(() => {});
  }, [waxAccount]);

  // ── Section A helpers ─────────────────────────────────────────────────────
  const loadPlotA = useCallback(async (x: number, y: number) => {
    setLoadingA(true); setErrorA(''); setPlotA(null); setPickA(null); setMsgA('');
    try {
      const d = await fetch(`/samatvam/api/samatvam/atavi-forest/plot?x=${x}&y=${y}`).then(r => r.json());
      if (d.success) { setPlotA(d); setSelA({ x, y }); } else setErrorA(d.error || 'Failed to load plot');
    } catch { setErrorA('Network error'); }
    setLoadingA(false);
  }, []);

  const goToPlotA = () => {
    const x = parseInt(xA), y = parseInt(yA);
    if (isNaN(x) || isNaN(y) || x < 0 || x > 99 || y < 0 || y > 99) { setErrorA('Enter valid x,y (0–99)'); return; }
    loadPlotA(x, y);
  };

  const goNextPartial = async () => {
    if (!myPlots.length) return;
    const start = partialCursor.current % myPlots.length;
    for (let i = 0; i < myPlots.length; i++) {
      const idx = (start + i) % myPlots.length;
      const p = myPlots[idx];
      const d = await fetch(`/samatvam/api/samatvam/atavi-forest/plot?x=${p.x}&y=${p.y}`).then(r => r.json());
      if ((d.owner_trees?.length ?? 0) < 5) {
        partialCursor.current = idx + 1;
        setXA(String(p.x)); setYA(String(p.y));
        setPlotA(d); setSelA({ x: p.x, y: p.y }); setErrorA(''); setPickA(null); setMsgA('');
        return;
      }
    }
    setErrorA('All your plots are fully planted');
  };

  const goNextFull = async () => {
    if (!myPlots.length) return;
    const start = fullCursor.current % myPlots.length;
    for (let i = 0; i < myPlots.length; i++) {
      const idx = (start + i) % myPlots.length;
      const p = myPlots[idx];
      const d = await fetch(`/samatvam/api/samatvam/atavi-forest/plot?x=${p.x}&y=${p.y}`).then(r => r.json());
      if ((d.owner_trees?.length ?? 0) >= 5) {
        fullCursor.current = idx + 1;
        setXA(String(p.x)); setYA(String(p.y));
        setPlotA(d); setSelA({ x: p.x, y: p.y }); setErrorA(''); setPickA(null); setMsgA('');
        return;
      }
    }
    setErrorA('No fully planted plots found');
  };

  const harvestOwner = async (slotIndex: number) => {
    if (!selA) return;
    setStatusA('planting'); setMsgA('');
    try {
      const d = await fetch('/samatvam/api/samatvam/atavi-forest/harvest', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAccount, x: selA.x, y: selA.y, tree_type: 'owner', slot_index: slotIndex }),
      }).then(r => r.json());
      if (d.success) {
        setStatusA('done'); setMsgA(`🪵 Harvested! +${d.logs_gained} logs (${d.species})`);
        await loadPlotA(selA.x, selA.y);
        setTimeout(() => { setStatusA('idle'); setMsgA(''); }, 4000);
      } else {
        setStatusA('error'); setMsgA(`❌ ${d.error}`);
        setTimeout(() => { setStatusA('idle'); setMsgA(''); }, 4000);
      }
    } catch { setStatusA('error'); setMsgA('❌ Network error'); setTimeout(() => { setStatusA('idle'); setMsgA(''); }, 4000); }
  };

  const plantBhoomi = async (species: string) => {
    if (!selA || statusA === 'planting') return;
    setStatusA('planting'); setMsgA('');
    try {
      const d = await fetch('/samatvam/api/samatvam/atavi-forest/plant', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAccount, x: selA.x, y: selA.y, species }),
      }).then(r => r.json());
      if (d.success) {
        setStatusA('done'); setMsgA(`✅ ${SPECIES_INFO[species].emoji} ${SPECIES_INFO[species].name} planted!`);
        if (d.bhoomi_score !== undefined) setBhoomi(d.bhoomi_score);
        setPickA(null);
        await loadPlotA(selA.x, selA.y);
        setTimeout(() => { setStatusA('idle'); setMsgA(''); }, 3000);
      } else {
        setStatusA('error'); setMsgA(`❌ ${d.error}`);
        setTimeout(() => { setStatusA('idle'); setMsgA(''); }, 4000);
      }
    } catch { setStatusA('error'); setMsgA('❌ Network error'); setTimeout(() => { setStatusA('idle'); setMsgA(''); }, 4000); }
  };

  // ── Section B helpers ─────────────────────────────────────────────────────
  const loadPlotB = useCallback(async (x: number, y: number) => {
    setLoadingB(true); setErrorB(''); setPlotB(null); setPickB(null); setMsgB('');
    try {
      const d = await fetch(`/samatvam/api/samatvam/atavi-forest/plot?x=${x}&y=${y}`).then(r => r.json());
      if (d.success) { setPlotB(d); setSelB({ x, y }); } else setErrorB(d.error || 'Failed to load plot');
    } catch { setErrorB('Network error'); }
    setLoadingB(false);
  }, []);

  const goToPlotB = () => {
    const x = parseInt(xB), y = parseInt(yB);
    if (isNaN(x) || isNaN(y) || x < 0 || x > 99 || y < 0 || y > 99) { setErrorB('Enter valid x,y (0–99)'); return; }
    loadPlotB(x, y);
  };

  const goNextRentalSpace = async () => {
    setLoadingB(true); setErrorB('');
    try {
      const { x: cx, y: cy } = rentalCursor.current;
      const d = await fetch(
        `/samatvam/api/samatvam/atavi-forest/find-rental?exclude=${encodeURIComponent(waxAccount)}&after_x=${cx}&after_y=${cy}`
      ).then(r => r.json());
      if (!d.success || !d.plot) { setErrorB('No rental plots found'); setLoadingB(false); return; }
      const { x, y } = d.plot;
      rentalCursor.current = { x, y };
      setXB(String(x)); setYB(String(y));
      const pd = await fetch(`/samatvam/api/samatvam/atavi-forest/plot?x=${x}&y=${y}`).then(r => r.json());
      if (pd.success) { setPlotB(pd); setSelB({ x, y }); setPickB(null); setMsgB(''); }
    } catch { setErrorB('Network error'); }
    setLoadingB(false);
  };

  const harvestRental = async (slotIndex: number) => {
    if (!selB) return;
    setStatusB('planting'); setMsgB('');
    try {
      const d = await fetch('/samatvam/api/samatvam/atavi-forest/harvest', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAccount, x: selB.x, y: selB.y, tree_type: 'rental', slot_index: slotIndex }),
      }).then(r => r.json());
      if (d.success) {
        setStatusB('done'); setMsgB(`🪵 Harvested! +${d.logs_gained} logs (${d.species})`);
        await loadPlotB(selB.x, selB.y);
        setTimeout(() => { setStatusB('idle'); setMsgB(''); }, 4000);
      } else {
        setStatusB('error'); setMsgB(`❌ ${d.error}`);
        setTimeout(() => { setStatusB('idle'); setMsgB(''); }, 4000);
      }
    } catch { setStatusB('error'); setMsgB('❌ Network error'); setTimeout(() => { setStatusB('idle'); setMsgB(''); }, 4000); }
  };

  const plantSeed = async (species: string) => {
    if (!selB || statusB === 'planting') return;
    setStatusB('planting'); setMsgB('');
    try {
      const d = await fetch('/samatvam/api/samatvam/atavi-forest/plant-rental', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAccount, x: selB.x, y: selB.y, species }),
      }).then(r => r.json());
      if (d.success) {
        setStatusB('done'); setMsgB(`✅ ${SPECIES_INFO[species].emoji} ${SPECIES_INFO[species].name} planted!`);
        if (d.seeds_in_wallet !== undefined) setSeeds(d.seeds_in_wallet);
        setPickB(null);
        await loadPlotB(selB.x, selB.y);
        setTimeout(() => { setStatusB('idle'); setMsgB(''); }, 3000);
      } else {
        setStatusB('error'); setMsgB(`❌ ${d.error}`);
        setTimeout(() => { setStatusB('idle'); setMsgB(''); }, 4000);
      }
    } catch { setStatusB('error'); setMsgB('❌ Network error'); setTimeout(() => { setStatusB('idle'); setMsgB(''); }, 4000); }
  };

  const isOwnA = plotA?.owner === waxAccount;
  const isOwnB = plotB?.owner === waxAccount;
  const ownerSlotsA: (TreeEntry | null)[]   = Array.from({ length: 5 }, (_, i) => (plotA?.owner_trees  ?? [])[i] ?? null);
  const rentalSlotsB: (RentalEntry | null)[] = Array.from({ length: 5 }, (_, i) => (plotB?.rental_trees ?? [])[i] ?? null);
  const now = Date.now();

  const NAV_BTN = (label: string, onClick: () => void, color = '#94a3b8', border = 'rgba(255,255,255,0.12)') => (
    <button onClick={onClick} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${border}`, background: 'rgba(255,255,255,0.04)', color, fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>{label}</button>
  );

  const COORD_INPUT = (val: string, setVal: (s: string) => void, label: string, onEnter: () => void) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontSize: 10, color: '#64748b' }}>{label}</span>
      <input value={val} onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') onEnter(); }}
        style={{ width: 50, padding: '5px 7px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(15,23,42,0.8)', color: 'white', fontSize: 13, fontFamily: 'monospace', textAlign: 'center' }}
        placeholder="0–99" />
    </div>
  );

  const inner = (
    <div style={{ background: '#0a1628', border: inline ? 'none' : '1px solid rgba(251,191,36,0.2)', borderRadius: inline ? 0 : 18, width: '100%', maxWidth: inline ? '100%' : 580, maxHeight: inline ? 'none' : '92vh', overflowY: inline ? 'visible' : 'auto', padding: 24, position: 'relative' }}
      onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, color: '#fbbf24', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}>⬡ Atavi Forest</div>
            <div style={{ fontSize: 20, color: 'white', fontWeight: 900, marginTop: 2 }}>Select Plot</div>
            <div style={{ fontSize: 11, color: '#475569', fontFamily: 'monospace', marginTop: 2 }}>{waxAccount}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 22, cursor: 'pointer', padding: 4 }}>✕</button>
        </div>

        {/* Balances row */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {bhoomi !== null && (
            <div style={{ flex: 1, minWidth: 130, background: 'rgba(217,119,6,0.1)', border: '1px solid rgba(217,119,6,0.2)', borderRadius: 10, padding: '8px 14px' }}>
              <div style={{ fontSize: 9, color: '#92400e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Bhoomi Balance</div>
              <div style={{ fontSize: 20, color: '#fbbf24', fontWeight: 900, marginTop: 2 }}>🪙 {bhoomi.toFixed(1)}</div>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 130, background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 10, padding: '8px 14px' }}>
            <div style={{ fontSize: 9, color: '#7c3aed', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Seed Balance</div>
            <div style={{ fontSize: 20, color: '#a78bfa', fontWeight: 900, marginTop: 2 }}>🌰 {seeds ?? '…'}</div>
          </div>
          <div style={{ flex: 1, minWidth: 130, background: 'rgba(251,191,36,0.05)', border: '1px solid rgba(251,191,36,0.12)', borderRadius: 10, padding: '8px 14px' }}>
            <div style={{ fontSize: 9, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>My Forest Plots</div>
            <div style={{ fontSize: 20, color: '#94a3b8', fontWeight: 900, marginTop: 2 }}>🏕️ {totalOwned}</div>
          </div>
        </div>

        {/* ══ SECTION A: My Plots ══════════════════════════════════════════════ */}
        <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ fontSize: 13, color: '#fbbf24', fontWeight: 900, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
            🌿 Select My Plots
          </div>

          {/* Nav row A */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
            {COORD_INPUT(xA, setXA, 'x', goToPlotA)}
            {COORD_INPUT(yA, setYA, 'y', goToPlotA)}
            {NAV_BTN('Go →', goToPlotA)}
            {NAV_BTN('Next Partial ↻', goNextPartial, '#fbbf24', 'rgba(251,191,36,0.25)')}
            {NAV_BTN('Next Full ↻', goNextFull, '#a5b4fc', 'rgba(99,102,241,0.25)')}
          </div>
          {errorA && <div style={{ fontSize: 11, color: '#f87171', marginBottom: 8 }}>{errorA}</div>}
          {loadingA && <div style={{ fontSize: 12, color: '#475569', padding: '12px 0' }}>Loading…</div>}

          {!loadingA && selA && plotA && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace', fontWeight: 700 }}>({selA.x}, {selA.y})</span>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: isOwnA ? 'rgba(251,191,36,0.12)' : 'rgba(99,102,241,0.12)', color: isOwnA ? '#fbbf24' : '#a5b4fc', fontFamily: 'monospace', fontWeight: 700 }}>
                  {isOwnA ? '✦ Your plot' : `Owner: ${plotA.owner ?? 'unowned'}`}
                </span>
              </div>

              {msgA && (
                <div style={{ padding: '7px 10px', borderRadius: 8, marginBottom: 10, fontSize: 12, fontWeight: 700,
                  background: statusA === 'done' ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)',
                  color: statusA === 'done' ? '#4ade80' : '#f87171' }}>{msgA}</div>
              )}

              <div style={{ fontSize: 10, color: '#d97706', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>🪙 Bhoomi Trees (Owner)</div>
              <OwnerSlots slots={ownerSlotsA} isOwn={isOwnA} pickingSlot={pickA} setPickingSlot={setPickA} planting={statusA === 'planting'} onHarvest={harvestOwner} />

              {pickA !== null && isOwnA && (
                <div style={{ marginTop: 10, padding: 12, borderRadius: 12, background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(251,191,36,0.2)' }}>
                  <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>Choose Species — costs Bhoomi</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
                    {Object.entries(SPECIES_INFO).map(([id, sp]) => {
                      const ok = bhoomi !== null && bhoomi >= sp.cost;
                      return (
                        <button key={id} onClick={() => ok && plantBhoomi(id)} disabled={!ok || statusA === 'planting'}
                          style={{ padding: '8px 4px', borderRadius: 10, border: `1px solid ${ok ? sp.color + '44' : 'rgba(255,255,255,0.06)'}`, background: ok ? `${sp.color}11` : 'rgba(15,23,42,0.4)', color: ok ? sp.color : '#334155', cursor: ok ? 'pointer' : 'not-allowed', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, opacity: ok ? 1 : 0.45 }}>
                          <span style={{ fontSize: 20 }}>{sp.emoji}</span>
                          <span style={{ fontSize: 9, fontWeight: 700 }}>{sp.name}</span>
                          <span style={{ fontSize: 9, color: ok ? '#d97706' : '#475569' }}>🪙{sp.cost}</span>
                          <span style={{ fontSize: 8, color: '#475569' }}>{sp.maturity_h}h</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ══ SECTION B: Find Plots for Rental ════════════════════════════════ */}
        <div>
          <div style={{ fontSize: 13, color: '#a78bfa', fontWeight: 900, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
            🌰 Find Plots for Rental
          </div>

          {/* Nav row B */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
            {COORD_INPUT(xB, setXB, 'x', goToPlotB)}
            {COORD_INPUT(yB, setYB, 'y', goToPlotB)}
            {NAV_BTN('Go →', goToPlotB)}
            {NAV_BTN('Next with Space ↻', goNextRentalSpace, '#a78bfa', 'rgba(167,139,250,0.3)')}
          </div>
          {errorB && <div style={{ fontSize: 11, color: '#f87171', marginBottom: 8 }}>{errorB}</div>}
          {loadingB && <div style={{ fontSize: 12, color: '#475569', padding: '12px 0' }}>Loading…</div>}

          {!loadingB && selB && plotB && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace', fontWeight: 700 }}>({selB.x}, {selB.y})</span>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20,
                  background: isOwnB ? 'rgba(239,68,68,0.12)' : 'rgba(167,139,250,0.12)',
                  color: isOwnB ? '#f87171' : '#a78bfa', fontFamily: 'monospace', fontWeight: 700 }}>
                  {isOwnB ? '⚠ Your own plot' : `Owner: ${plotB.owner ?? 'unowned'}`}
                </span>
              </div>

              {isOwnB && (
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10, padding: '6px 10px', borderRadius: 8, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
                  This is your own plot. Use the <strong style={{ color: '#fbbf24' }}>Select My Plots</strong> section above to plant bhoomi trees here.
                </div>
              )}

              {msgB && (
                <div style={{ padding: '7px 10px', borderRadius: 8, marginBottom: 10, fontSize: 12, fontWeight: 700,
                  background: statusB === 'done' ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)',
                  color: statusB === 'done' ? '#4ade80' : '#f87171' }}>{msgB}</div>
              )}

              <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>🌰 Seed Trees (Rental Slots)</div>
              <RentalSlots
                slots={rentalSlotsB}
                waxAccount={waxAccount}
                canPlant={!isOwnB && (seeds ?? 0) > 0}
                pickingSlot={pickB}
                setPickingSlot={setPickB}
                planting={statusB === 'planting'}
                onHarvest={harvestRental}
              />

              {pickB !== null && !isOwnB && (
                <div style={{ marginTop: 10, padding: 12, borderRadius: 12, background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(167,139,250,0.2)' }}>
                  <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Choose Species</div>
                  <div style={{ fontSize: 10, color: '#7c3aed', marginBottom: 10 }}>Each tree costs <strong style={{ color: '#a78bfa' }}>1 seed</strong> — you have <strong style={{ color: '#a78bfa' }}>{seeds ?? 0} seeds</strong></div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
                    {Object.entries(SPECIES_INFO).map(([id, sp]) => {
                      const ok = (seeds ?? 0) > 0;
                      return (
                        <button key={id} onClick={() => ok && plantSeed(id)} disabled={!ok || statusB === 'planting'}
                          style={{ padding: '8px 4px', borderRadius: 10, border: `1px solid ${ok ? sp.color + '44' : 'rgba(255,255,255,0.06)'}`, background: ok ? `${sp.color}11` : 'rgba(15,23,42,0.4)', color: ok ? sp.color : '#334155', cursor: ok ? 'pointer' : 'not-allowed', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, opacity: ok ? 1 : 0.45 }}>
                          <span style={{ fontSize: 20 }}>{sp.emoji}</span>
                          <span style={{ fontSize: 9, fontWeight: 700 }}>{sp.name}</span>
                          <span style={{ fontSize: 9, color: ok ? '#a78bfa' : '#475569' }}>🌰 1 seed</span>
                          <span style={{ fontSize: 8, color: '#475569' }}>{sp.maturity_h}h</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

      </div>
  );

  if (inline) return inner;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={() => { if (pickA !== null) { setPickA(null); return; } if (pickB !== null) { setPickB(null); return; } onClose(); }}>
      {inner}
    </div>
  );
}
